import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { TrendingDown, ArrowRight, Sparkles } from "lucide-react";
import {
  billableDays,
  parseDateTimeLocal,
  formatDateTimeLocal,
  formatYMD,
  rangeOverlapsRanges,
  rangeHitsHostCalendar,
  bookingWindowError,
  formatTripRange,
} from "../lib/dateLocal";
import { formatNumber } from "../lib/currencyDisplay";

/**
 * Smart Reschedule — given selected start/end datetimes, propose a same-duration
 * window within ±7 days that's cheaper based on per-day demand forecast.
 * Only suggests windows that pass the same availability checks as checkout.
 */
const SAVINGS_THRESHOLD = 20; // dollars
const MAX_SHIFT_DAYS = 7;

function dateOnly(s) {
  return s ? s.split("T")[0] : "";
}

function addDaysISO(iso, n) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function shiftWindow(startStr, endStr, shiftDays) {
  const sdt = parseDateTimeLocal(startStr);
  const edt = parseDateTimeLocal(endStr);
  if (!sdt || !edt) return null;
  const shiftedStart = new Date(sdt);
  shiftedStart.setDate(shiftedStart.getDate() + shiftDays);
  const shiftedEnd = new Date(edt);
  shiftedEnd.setDate(shiftedEnd.getDate() + shiftDays);
  return {
    start: formatDateTimeLocal(shiftedStart),
    end: formatDateTimeLocal(shiftedEnd),
  };
}

function isBookableWindow(startStr, endStr, bookedRanges, calendarMode, calendarDays, listingPauses) {
  if (!startStr || !endStr) return false;
  if (bookingWindowError(startStr, endStr)) return false;
  if (rangeOverlapsRanges(startStr, endStr, bookedRanges)) return false;
  if (rangeHitsHostCalendar(startStr, endStr, calendarMode, calendarDays, listingPauses)) return false;
  return true;
}

export default function SmartReschedule({
  vehicleId,
  start,
  end,
  bookedRanges = [],
  calendarMode = "block_unavailable",
  calendarDays = [],
  listingPauses = [],
  onApply,
}) {
  const [forecast, setForecast] = useState(null);

  useEffect(() => {
    if (!vehicleId) return;
    axios.get(`${API}/vehicles/${vehicleId}/demand-forecast?days=21`)
      .then(r => setForecast(r.data))
      .catch(() => setForecast(null));
  }, [vehicleId]);

  const suggestion = useMemo(() => {
    if (!forecast || !start || !end) return null;
    const sdt = parseDateTimeLocal(start);
    const edt = parseDateTimeLocal(end);
    if (!sdt || !edt || edt <= sdt) return null;

    const durationDays = billableDays(start, end);
    if (durationDays < 1) return null;

    const byDate = {};
    forecast.days.forEach(d => { byDate[d.date] = d; });

    const sumBillableDays = (startIso) => {
      let total = 0;
      for (let i = 0; i < durationDays; i++) {
        const k = addDaysISO(startIso, i);
        const d = byDate[k];
        if (!d) return null;
        total += d.rate;
      }
      return total;
    };

    const currentStartDate = dateOnly(start);
    const current = sumBillableDays(currentStartDate);
    if (current == null) return null;

    const today = formatYMD(new Date());
    let best = null;

    for (let shift = -MAX_SHIFT_DAYS; shift <= MAX_SHIFT_DAYS; shift++) {
      if (shift === 0) continue;
      const window = shiftWindow(start, end, shift);
      if (!window) continue;
      if (!isBookableWindow(window.start, window.end, bookedRanges, calendarMode, calendarDays, listingPauses)) continue;

      const candStartDate = dateOnly(window.start);
      if (candStartDate < today) continue;

      const tot = sumBillableDays(candStartDate);
      if (tot == null) continue;
      if (!best || tot < best.total) {
        best = {
          start: window.start,
          end: window.end,
          startDate: candStartDate,
          endDate: dateOnly(window.end),
          total: tot,
          shiftDays: shift,
        };
      }
    }

    if (!best) return null;

    const savings = current - best.total;
    if (savings < SAVINGS_THRESHOLD) return null;

    return {
      ...best,
      savings,
      current,
      durationDays,
    };
  }, [forecast, start, end, bookedRanges, calendarMode, calendarDays, listingPauses]);

  if (!suggestion) return null;

  const direction = suggestion.shiftDays > 0 ? "later" : "earlier";
  const shiftLabel = Math.abs(suggestion.shiftDays);

  return (
    <div className="zc-card p-4 mt-4" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, rgba(22,163,74,0.08) 100%)" }} data-testid="smart-reschedule">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 grid place-items-center rounded-lg bg-[var(--zc-success)] text-white flex-shrink-0">
          <TrendingDown size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Sparkles size={11} className="text-[var(--zc-accent)]" />
            <div className="eyebrow">Save ${Math.round(suggestion.savings)} with smart rescheduling</div>
          </div>
          <p className="text-sm mt-1 leading-snug">
            Shift this {suggestion.durationDays === 1 ? "1-day" : `${suggestion.durationDays}-day`} trip{" "}
            <span className="font-semibold">{shiftLabel} day{shiftLabel > 1 ? "s" : ""} {direction}</span>{" "}
            ({formatTripRange(suggestion.start, suggestion.end) || "—"}) and pay{" "}
            <span className="font-semibold text-[var(--zc-success)]">${formatNumber(suggestion.total, 0)}</span> instead of ${formatNumber(suggestion.current, 0)}.
          </p>
          <button
            type="button"
            onClick={() => onApply?.(suggestion.start, suggestion.end)}
            className="mt-3 inline-flex items-center gap-1.5 px-3 h-9 zc-btn-primary text-xs"
            data-testid="smart-reschedule-apply"
          >
            Apply suggested dates <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
